import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Container, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import Can from 'components/Can';
import Layout from 'components/Layout';
import Feature from 'components/Feature';
import Brokers from 'components/Brokers';
import Comments from 'components/Comments';
import { AlarmsWidget } from 'components/Alarms';
import useStyles from './CustomersDetails.styled';
import { usersActions } from 'state/modules/users';
import { withRouter, useParams } from 'react-router-dom';
import { ReservationsWidget } from 'components/Reservations';
import { SubscriptionsWidget } from 'components/Subscriptions';
import { UserLogs, VehicleGroups, UserGroups } from 'components/Users';
import { Details, Accounts, CreditsWidget, Qualifications } from 'components/Customers';
import { PaymentMethods } from 'components/PaymentMethods';
import DocumentsWidget from 'components/Documents/Widget';
import { NFCDetailsWidget } from 'components/AccessDevice';

const CustomersDetails = ({ history }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { id } = useParams();
  const user = useSelector((state) => state.users.user);
  const userPrivileges = useSelector((state) => state.users.userPrivileges);
  const payment_methods_allowed =
    useSelector((state) => state?.config?.settings?.core?.payment?.payment_methods_allowed) || [];
  const user_access_via_ug =
    useSelector((state) => state?.config?.settings?.core?.vehicle_group?.user_access_via_ug) ||
    false;
  const { account } = useSelector((state) => state.customers || {});
  const hasReservationInvoice = useSelector(
    (state) => state.config?.settings?.core?.reservation?.invoice?.enabled || false
  );
  const hasInvoice = useSelector(
    (state) => state.config?.settings?.core?.invoice?.enabled || false
  );

  useEffect(() => {
    dispatch(usersActions.fetchUserPrivileges(id));
  }, [id, dispatch]);

  const requiredVGWidget = userPrivileges?.vehicle_group
    ? userPrivileges?.vehicle_group.find((val) => val !== '*' && val !== 'access_all')
    : true;

  const description = `${user?.first_name || ''} ${user?.last_name || ''}`;

  return (
    <Layout title="Customer">
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container direction="row" justifyContent="space-between" spacing={2}>
              <Grid item>
                <Typography variant="h1" gutterBottom>
                  <Box fontWeight="fontWeightBold" fontSize={24} mb={1}>
                    {i18n.t('common.detail_title', { description, id })}
                  </Box>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item sm={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Details id={id} />
                  </Grid>
                  {hasInvoice ? (
                    <>
                      <Feature name="account" is="enabled">
                        <Can I="read" on="account">
                          <Grid item xs={12}>
                            <Accounts />
                          </Grid>
                        </Can>
                      </Feature>

                      <Feature name="credit" is="enabled">
                        <Can I="read" on="credit">
                          <Grid item xs={12}>
                            <CreditsWidget user={+id === user?.id ? user : null} />
                          </Grid>
                        </Can>
                      </Feature>
                    </>
                  ) : null}
                  <Can I="read" on="user_group">
                    <Grid item xs={12}>
                      <UserGroups id={id} selectCustomParams={{ user_group_type: 'DRIVER' }} />
                    </Grid>
                  </Can>
                </Grid>
              </Grid>
              <Grid item sm={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Feature name="reservation" is="enabled">
                    <Grid item xs={12}>
                      <ReservationsWidget
                        type="user"
                        history={history}
                        customParams={{ user_id: id }}
                      />
                    </Grid>
                  </Feature>
                  <Feature name="subscription" is="enabled">
                    <Grid item xs={12}>
                      <SubscriptionsWidget history={history} customParams={{ user_id: id }} />
                    </Grid>
                  </Feature>
                  {requiredVGWidget && (
                    <>
                      <Grid item xs={12}>
                        <VehicleGroups id={id} />
                      </Grid>
                      {user_access_via_ug ? (
                        <Grid item xs={12}>
                          <VehicleGroups id={id} type="INHERITED" editable={false} />
                        </Grid>
                      ) : null}
                    </>
                  )}
                  <Can I="read" a="conditional_instance">
                    <Grid item xs={12}>
                      <AlarmsWidget type="user" id={parseInt(id)} />
                    </Grid>
                  </Can>
                  <Feature name="access_device" is="nfc_allowed">
                    <Grid item xs={12}>
                      <NFCDetailsWidget user={user} view="customer" />
                    </Grid>
                  </Feature>
                  <Grid item xs={12}>
                    <DocumentsWidget type="user" id={parseInt(id)} />
                  </Grid>
                  <Can I="read" on="event_log">
                    <Grid item xs={12}>
                      <UserLogs title={i18n.t('customers.logs')} id={id} shared={true} />
                    </Grid>
                  </Can>
                </Grid>
              </Grid>
              <Grid item sm={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Qualifications id={id} />
                  </Grid>
                  {user?.account_id &&
                    Boolean(payment_methods_allowed.length) &&
                    hasReservationInvoice && (
                      <Can I="read" on="payment_method">
                        <Grid item xs={12}>
                          <PaymentMethods modelId={user?.id} accountId={user?.account_id} />
                        </Grid>
                      </Can>
                    )}
                  <Can I="read" a="comment">
                    <Grid item xs={12}>
                      <Comments type="user" id={parseInt(id)} />
                    </Grid>
                  </Can>
                  <Can I="read" on="user">
                    <Grid item xs={12}>
                      <Brokers
                        id={id}
                        type="user"
                        title={i18n.t('customers.user_brokers')}
                        brokers={user?.brokers || {}}
                      />
                    </Grid>
                  </Can>
                  {hasReservationInvoice ? (
                    <Can I="read" on="user">
                      <Grid item xs={12}>
                        <Brokers
                          id={account?.id}
                          type="account"
                          title={i18n.t('customers.account_brokers')}
                          brokers={account?.brokers || {}}
                        />
                      </Grid>
                    </Can>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

CustomersDetails.propTypes = {
  history: PropTypes.object,
};

export default withRouter(CustomersDetails);
